// Self-contained brand tokens for driver-app
import { createTheme, alpha } from "@mui/material/styles";

export const brandTokens = {
    navy: "#0F1B2D",
    navyLight: "#1A2D45",
    navyDark: "#080E19",
    amber: "#F59E0B",
    amberDark: "#D97706",
    amberLight: "#FCD34D",
    surface: "#FFFFFF",
    muted: "#F1F5F9",
    mutedDark: "#E2E8F0",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    textMuted: "#94A3B8",
};

export const createBrandTheme = (primaryOverride?: string) =>
    createTheme({
        palette: {
            primary: {
                main: primaryOverride || brandTokens.navy,
                light: brandTokens.navyLight,
                dark: brandTokens.navyDark,
                contrastText: "#FFFFFF",
            },
            secondary: {
                main: brandTokens.amber,
                light: brandTokens.amberLight,
                dark: brandTokens.amberDark,
                contrastText: brandTokens.navy,
            },
            background: { default: brandTokens.muted, paper: brandTokens.surface },
            text: { primary: brandTokens.textPrimary, secondary: brandTokens.textSecondary },
            divider: "#E2E8F0",
            success: { main: "#10B981", light: "#D1FAE5", dark: "#059669", contrastText: "#fff" },
            warning: { main: brandTokens.amber, light: "#FEF3C7", dark: "#B45309", contrastText: "#fff" },
            error: { main: "#EF4444", light: "#FEE2E2", dark: "#DC2626", contrastText: "#fff" },
            info: { main: "#3B82F6", light: "#DBEAFE", dark: "#2563EB", contrastText: "#fff" },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
            button: { fontWeight: 600, textTransform: "none" },
        },
        shape: { borderRadius: 10 },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: { backgroundColor: brandTokens.navy, backgroundImage: "none" },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { textTransform: "none", borderRadius: 8, fontWeight: 600 },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: "none", borderRadius: 12, border: "1px solid #E2E8F0" },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: { borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(15,27,45,0.06)" },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 600, fontSize: "0.75rem", borderRadius: 6, height: 26 },
                    colorSuccess: { backgroundColor: "#D1FAE5", color: "#065F46" },
                    colorWarning: { backgroundColor: "#FEF3C7", color: "#92400E" },
                    colorError: { backgroundColor: "#FEE2E2", color: "#991B1B" },
                    colorInfo: { backgroundColor: "#DBEAFE", color: "#1E40AF" },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: 16, boxShadow: "0 20px 60px rgba(15,27,45,0.2)" },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        backgroundColor: brandTokens.navy,
                        color: "#FFFFFF",
                        padding: "16px 24px",
                        fontSize: "1rem",
                        fontWeight: 600,
                    },
                },
            },
            MuiTextField: {
                defaultProps: { size: "small" },
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 8,
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brandTokens.amber },
                            "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(brandTokens.amber, 0.15)}` },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: brandTokens.amber },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } },
            },
        },
    });

export const brandTheme = createBrandTheme();
